import { useRef } from "react"

import { HelpCenterContent, HelpCenterHeader, WikiSection, DownloadCard } from "@/areas/help-center"
import Article from "@/shared/layouts/Article/Article"

function LinkingFocusSetup() {
  const contentRef = useRef<HTMLDivElement>(null)

  return (
    <HelpCenterContent ref={contentRef}>
      <HelpCenterHeader
        title="OCB Desktop App Setup"
        subtitle="Required Focus POS configurations to ensure correct order processing."
        logo="/static/wiki/focus-logo.png"
        backPath="/help-center/linking-pos-devices"
        backLabel="Linking POS Devices"
        contentRef={contentRef}
      />

      <WikiSection id="overview" title="OVERVIEW">
        <Article>
          <p>The following process is going to explain step by:</p>
          <ul className="wiki-list">
            <li>Update the OCB app using the correct version;</li>
            <li>Logging into the app using the default password;</li>
            <li>Connecting and Configuring the POS to connect to Windows-identified devices (speakers, recording devices);</li>
            <li>Linking the OCB App to the Focus POS System.</li>
          </ul>

          <h3>Requirements</h3>
          <p>To successfully complete this setup, you will need:</p>
          <ul className="wiki-list">
            <li>Access to the <strong>company's Windows machine</strong> where the OCB App will be installed;</li>
            <li>Focus POS already configured and running on the same Windows machine.</li>
          </ul>
        </Article>
      </WikiSection>

      <WikiSection id="download-install-ocb" title="A - DOWNLOAD AND INSTALL THE OCB DESKTOP APP">
        <Article>
          <p>In our Download Hub, you'll find a variety of essential downloads. After downloading the OCB app, extract the files, run the .exe installer, and complete the installation steps.</p>

          <DownloadCard
            name="OCB App"
            version="v.0.41.2"
            releaseDate="Month, DD, YYYY"
            downloadUrl="#"
          />

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>After downloading, extract the files, run the <strong>.exe installer</strong>, and complete the installation steps.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Once installed, locate the OCB app on your computer and launch it.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>The application will automatically start loading and preparing for use.</p>
          </div>

          <p>See below for a visual representation of the OCB App loading screen.</p>
        </Article>
      </WikiSection>

      <WikiSection id="open-access-configuration" title="B — OPEN THE OCB APP AND ACCESS THE CONFIGURATION FLOW">
        <Article>
          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Open the <strong>OCB App</strong> on your desktop.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>You will be presented with the main screen. From here, you can access the application settings and configuration panel.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>If this is your first time using the app, you may be prompted to configure your screen ID.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Back to the main screen, click on the settings icon or open the configuration panel to begin the setup process.</p>
          </div>

          <div className="wiki-image-single">
            <img src="/static/wiki/oracle/linking-oracle-01.png" alt="OCB App welcome screen" className="wiki-screenshot" />
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Click on the <strong>Settings</strong> icon.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>The settings panel will display on the left with various options such as <strong>Text Settings</strong>, <strong>Background Settings</strong>, and <strong>Other Settings</strong>.</p>
          </div>

          <div className="wiki-image-single">
            <img src="/static/wiki/oracle/linking-oracle-02.png" alt="OCB settings panel" className="wiki-screenshot" />
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Navigate to the <strong>password</strong> field.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Enter the password provided by the Stream team to proceed.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Once authenticated, you&apos;ll be able to access the configuration wizard to link your Focus POS.</p>
          </div>

          <div className="wiki-image-single">
            <img src="/static/wiki/oracle/linking-oracle-03.png" alt="Password entry screen" className="wiki-screenshot" />
          </div>
        </Article>
      </WikiSection>

      <WikiSection id="configure-ocb-focus" title="C - CONFIGURE OCB APP FOR FOCUS POS">
        <Article>
          <h3>Select Focus Integration</h3>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>After entering your password, you will be directed to the integration wizard.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>On the first step, you&apos;ll see a screen titled <strong>CHOOSE POINT OF SALES</strong>. This is where you select which POS system you want to integrate with.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Click on the dropdown menu and select <strong>Focus Integration</strong> from the list of available POS systems.</p>
          </div>

          <div className="wiki-image-flow">
            <img src="/static/wiki/oracle/linking-oracle-04.png" alt="Choose Point of Sales screen" className="wiki-screenshot" />
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-flow-arrow" />
            <img src="/static/wiki/focus/linking-focus-05.png" alt="POS dropdown menu with Focus" className="wiki-screenshot" />
          </div>

          <div className="wiki-image-single">
            <img src="/static/wiki/focus/linking-focus-06.png" alt="Focus Integration selected" className="wiki-screenshot" />
          </div>

          <h3>Define Integration Parameters</h3>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>After selecting Focus Integration, click the <strong>Next</strong> button to proceed to the configuration form.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>On step 2, you will be prompted to <strong>ENTER YOUR FOCUS POS IDENTIFIER</strong>. This unique identifier links the OCB App to your Focus POS system installation.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>You&apos;ll also see an option to <strong>ENABLE SOUNDHOUND</strong>. Toggle this switch if you want to enable voice ordering capabilities through SoundHound integration.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>After filling in your Focus POS identifier, click the <strong>Next</strong> button to proceed.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>You will be redirected to the confirmation screen.</p>
          </div>

          <div className="wiki-image-flow">
            <img src="/static/wiki/focus/linking-focus-07.png" alt="Enter Focus POS identifier" className="wiki-screenshot" />
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-flow-arrow" />
            <img src="/static/wiki/focus/linking-focus-08.png" alt="Confirm your choices" className="wiki-screenshot" />
          </div>

          <h3>Validate OCB App Status</h3>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>On the final step, you will see a <strong>CONFIRM YOUR CHOICES</strong> screen that displays all the configuration details you&apos;ve entered.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Review all the information carefully to ensure everything is correct. Check the integration name and your Focus POS identifier.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Once you&apos;ve verified all details, click the <strong>Confirm</strong> button to complete the setup.</p>
          </div>

          <div className="wiki-image-single">
            <img src="/static/wiki/oracle/linking-oracle-09.png" alt="Setup complete - Welcome screen" className="wiki-screenshot" />
          </div>
        </Article>
      </WikiSection>
    </HelpCenterContent>
  )
}

export default LinkingFocusSetup
